import React, { useState, useEffect, useMemo } from "react";
import { ArrowBack, Assessment, CalendarToday, TrendingUp, Receipt } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    BackButton,
    ResultsCard,
    getTheme,
} from "../../components/common";

function DashboardPage() {
    const history = useHistory();
    const theme = getTheme('comerzzia');
    const [isLoading, setIsLoading] = useState(false);
    const [metrics, setMetrics] = useState([]);
    const [empresas, setEmpresas] = useState([]);

    // Filtros
    const currentYear = new Date().getFullYear();
    const [selectedCompany, setSelectedCompany] = useState('all');
    const [selectedYear, setSelectedYear] = useState(currentYear.toString());
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [companyChartMode, setCompanyChartMode] = useState('count');
    const [selectedMonth, setSelectedMonth] = useState("");

    useEffect(() => {
        let isMounted = true;

        const fetchEmpresas = async () => {
            try {
                const { data } = await fetchClient.get("/comerzzia/getAllAplicacao");
                if (isMounted) {
                    setEmpresas(data);
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error("Erro ao buscar empresas");
                }
            }
        };

        fetchEmpresas();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchMetrics = async () => {
            try {
                if (isMounted) {
                    setIsLoading(true);
                }

                const params = new URLSearchParams({
                    year: selectedYear,
                    companyId: selectedCompany
                });

                const { data } = await fetchClient.get(`/comerzzia/invoice-metrics?${params.toString()}`);
                if (isMounted) {
                    setMetrics(data || []);
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error(error.response?.data?.message || "Erro ao buscar métricas");
                    setMetrics([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (selectedCompany && selectedYear) {
            fetchMetrics();
        }

        return () => {
            isMounted = false;
        };
    }, [selectedCompany, selectedYear]);

    // Preparar dados para o gráfico de invoices
    const prepareInvoiceChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Invoices"]];
        }

        const chartData = [["Mês", "Invoices"]];
        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        filteredMetrics
            .sort((a, b) => parseInt(a.month) - parseInt(b.month))
            .forEach(metric => {
                const monthIndex = parseInt(metric.month) - 1;
                const monthName = monthNames[monthIndex] || metric.month;
                chartData.push([monthName, metric.transactions || 0]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico de valores
    const prepareAmountChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Valor Total"]];
        }

        const chartData = [["Mês", "Valor Total"]];
        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        filteredMetrics
            .sort((a, b) => parseInt(a.month) - parseInt(b.month))
            .forEach(metric => {
                const monthIndex = parseInt(metric.month) - 1;
                const monthName = monthNames[monthIndex] || metric.month;
                chartData.push([monthName, metric.totalValue || 0]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico por empresa (quando "all" está selecionado)
    const prepareCompanyChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0 || selectedCompany !== 'all') {
            return [["Empresa"]];
        }

        // Agrupar dados por empresa
        const companyData = {};

        filteredMetrics.forEach(metric => {
            const companyId = metric.companyId;
            const company = empresas.find(e => e.id === companyId);
            const companyName = company?.name || `Empresa ${companyId}`;

            if (!companyData[companyName]) {
                companyData[companyName] = {
                    transactions: 0,
                    totalValue: 0
                };
            }

            companyData[companyName].transactions += metric.transactions || 0;
            companyData[companyName].totalValue += metric.totalValue || 0;
        });

        // Preparar dados para o gráfico
        const chartData = [["Empresa", "Invoices", "Valor Total (R$)"]];

        Object.entries(companyData)
            .sort((a, b) => b[1].totalValue - a[1].totalValue) // Ordenar por valor total
            .forEach(([companyName, data]) => {
                chartData.push([
                    companyName,
                    data.transactions,
                    data.totalValue
                ]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico por empresa - apenas contagem
    const prepareCompanyCountData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0 || selectedCompany !== 'all') {
            return [["Empresa", "Invoices"]];
        }
        const companyData = {};
        filteredMetrics.forEach(metric => {
            const company = empresas.find(e => e.id === metric.companyId);
            const companyName = company?.name || `Empresa ${metric.companyId}`;
            if (!companyData[companyName]) companyData[companyName] = 0;
            companyData[companyName] += metric.transactions || 0;
        });
        const chartData = [["Empresa", "Invoices"]];
        Object.entries(companyData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([name, count]) => chartData.push([name, count]));
        return chartData;
    };

    // Preparar dados para o gráfico por empresa - apenas valor
    const prepareCompanyValueData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0 || selectedCompany !== 'all') {
            return [["Empresa", "Valor Total (R$)"]];
        }
        const companyData = {};
        filteredMetrics.forEach(metric => {
            const company = empresas.find(e => e.id === metric.companyId);
            const companyName = company?.name || `Empresa ${metric.companyId}`;
            if (!companyData[companyName]) companyData[companyName] = 0;
            companyData[companyName] += metric.totalValue || 0;
        });
        const chartData = [["Empresa", "Valor Total (R$)"]];
        Object.entries(companyData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([name, val]) => chartData.push([name, val]));
        return chartData;
    };

    // Preparar dados para o gráfico de evolução
    const prepareEvolutionChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Transações", "Valor Total (R$)"]];
        }

        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        // Agregar dados por mês (para caso de "all companies")
        const monthlyData = {};

        filteredMetrics.forEach(metric => {
            const month = metric.month;
            if (!monthlyData[month]) {
                monthlyData[month] = {
                    transactions: 0,
                    totalValue: 0
                };
            }
            monthlyData[month].transactions += metric.transactions || 0;
            monthlyData[month].totalValue += metric.totalValue || 0;
        });

        // Ordenar por mês
        const sortedMonths = Object.keys(monthlyData)
            .map(m => parseInt(m))
            .sort((a, b) => a - b);

        const chartData = [["Mês", "Transações", "Valor Total (R$)"]];

        // Adicionar dados reais
        sortedMonths.forEach(month => {
            const monthIndex = month - 1;
            const monthName = monthNames[monthIndex] || month;
            chartData.push([
                monthName,
                monthlyData[month].transactions,
                monthlyData[month].totalValue
            ]);
        });

        return chartData;
    };

    // Calcular totalizadores
    const calculateTotals = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return {
                transactions: 0,
                totalValue: 0,
                averagePerMonth: 0,
                averageAmount: 0
            };
        }

        const transactions = filteredMetrics.reduce((sum, m) => sum + (m.transactions || 0), 0);
        const totalValue = filteredMetrics.reduce((sum, m) => sum + (m.totalValue || 0), 0);
        const monthsWithData = filteredMetrics.length;

        return {
            transactions,
            totalValue,
            averagePerMonth: monthsWithData > 0 ? Math.round(transactions / monthsWithData) : 0,
            averageAmount: transactions > 0 ? (totalValue / transactions) : 0
        };
    };

    const filteredMetrics = useMemo(() => {
        if (!selectedMonth) return metrics;
        return metrics.filter(m => parseInt(m.month) === parseInt(selectedMonth));
    }, [metrics, selectedMonth]);

    const kpiVariation = useMemo(() => {
        const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
        if (!metrics.length) return { transactions: null, value: null, ticket: null };
        const grouped = {};
        metrics.forEach((m) => {
            const mo = parseInt(m.month);
            if (!grouped[mo]) grouped[mo] = { transactions: 0, totalValue: 0 };
            grouped[mo].transactions += m.transactions || 0;
            grouped[mo].totalValue += m.totalValue || 0;
        });
        const months = Object.keys(grouped).map(Number).sort((a, b) => a - b);
        if (months.length < 2) return { transactions: null, value: null, ticket: null };
        const last = grouped[months[months.length - 1]];
        const prev = grouped[months[months.length - 2]];
        const lastTicket = last.transactions > 0 ? last.totalValue / last.transactions : 0;
        const prevTicket = prev.transactions > 0 ? prev.totalValue / prev.transactions : 0;
        return {
            transactions: pct(last.transactions, prev.transactions),
            value: pct(last.totalValue, prev.totalValue),
            ticket: pct(lastTicket, prevTicket),
        };
    }, [metrics]);

    const sortedMetrics = useMemo(() => {
        return [...filteredMetrics].sort((a, b) => {
            if (selectedCompany === 'all') {
                const companyA = empresas.find(e => e.id === a.companyId)?.name || '';
                const companyB = empresas.find(e => e.id === b.companyId)?.name || '';
                if (companyA !== companyB) {
                    return companyA.localeCompare(companyB);
                }
            }
            return parseInt(a.month) - parseInt(b.month);
        });
    }, [filteredMetrics, selectedCompany, empresas]);

    const totalPages = Math.max(1, Math.ceil(sortedMetrics.length / rowsPerPage));
    const paginatedMetrics = sortedMetrics.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [metrics, rowsPerPage, selectedMonth]);

    const totals = calculateTotals();
    const selectedCompanyName = selectedCompany === 'all'
        ? 'Todas as empresas'
        : empresas.find(e => e.id === selectedCompany)?.name || '';

    const invoiceChartOptions = {
        title: `Invoices por Mês - ${selectedYear}`,
        chartArea: { width: "80%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Quantidade de Invoices",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        colors: ["#00bcd4"],
        legend: { position: "none" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const amountChartOptions = {
        title: `Valor Total por Mês - ${selectedYear}`,
        chartArea: { width: "80%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Valor Total (R$)",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
            format: "decimal",
        },
        colors: ["#667eea"],
        legend: { position: "none" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const companyChartOptions = {
        title: `Desempenho por Empresa - ${selectedYear}`,
        chartArea: { width: "70%", height: "70%" },
        hAxis: {
            title: "Empresa",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Valor",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        seriesType: "bars",
        series: {
            0: { color: "#00bcd4", targetAxisIndex: 0 }, // Invoices
            1: { color: "#667eea", targetAxisIndex: 1, type: "line" }, // Valor Total
        },
        vAxes: {
            0: { title: "Invoices", titleTextStyle: { color: "#00bcd4" } },
            1: { title: "Valor Total (R$)", titleTextStyle: { color: "#667eea" }, format: "decimal" },
        },
        legend: { position: "top" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const evolutionChartOptions = {
        title: `Evolução Mensal - ${selectedYear}`,
        chartArea: { width: "75%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Valor",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        seriesType: "line",
        series: {
            0: {
                color: "#00bcd4",
                targetAxisIndex: 0,
                lineWidth: 3,
                pointSize: 6
            }, // Transações
            1: {
                color: "#667eea",
                targetAxisIndex: 1,
                lineWidth: 3,
                pointSize: 6
            }, // Valor Total
        },
        vAxes: {
            0: {
                title: "Transações",
                titleTextStyle: { color: "#00bcd4" },
                gridlines: { color: "#f0f0f0" }
            },
            1: {
                title: "Valor Total (R$)",
                titleTextStyle: { color: "#667eea" },
                format: "decimal",
                gridlines: { color: "#f5f5f5" }
            },
        },
        legend: { position: "top" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const exportCSV = () => {
        const BOM = "\uFEFF";
        const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                            "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
        const hasCompanyCol = selectedCompany === 'all';
        const headers = (hasCompanyCol ? "Empresa;" : "") + "Mês;Invoices;Valor Total;Ticket Médio";
        const rows = sortedMetrics.map(metric => {
            const monthIndex = parseInt(metric.month) - 1;
            const monthName = monthNames[monthIndex] || metric.month;
            const company = empresas.find(e => e.id === metric.companyId);
            const companyName = company?.name || `Empresa ${metric.companyId}`;
            const ticketMedio = metric.transactions > 0 ? metric.totalValue / metric.transactions : 0;
            const cols = [
                ...(hasCompanyCol ? [companyName] : []),
                monthName,
                (metric.transactions || 0).toString(),
                (metric.totalValue || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
                ticketMedio.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
            ];
            return cols.join(";");
        });
        const csv = BOM + headers + "\n" + rows.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `comerzzia-dashboard-${selectedYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading && !metrics.length) return <Loader />;

    return (
        <Container>
            <ModernHeader>
                <HeaderLeft>
                    <BackButton color={theme.primary} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <PageTitle>
                            <Assessment style={{ marginRight: '12px', fontSize: '32px' }} />
                            Dashboard de Transações
                        </PageTitle>
                        <PageSubtitle>Visualize métricas de invoices por período</PageSubtitle>
                    </HeaderContent>
                </HeaderLeft>
            </ModernHeader>

            <ResultsCard>
            <FiltersCard>
                <FilterTitle>Filtros</FilterTitle>
                <FiltersContainer>
                    <FilterGroup>
                        <FilterLabel>Empresa</FilterLabel>
                        <FilterSelect
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                        >
                            <option value="">Selecione uma empresa</option>
                            <option value="all">Todas as empresas</option>
                            {empresas.map(empresa => (
                                <option key={empresa.id} value={empresa.id}>
                                    {empresa.name}
                                </option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Ano</FilterLabel>
                        <FilterSelect
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                        >
                            {[...Array(5)].map((_, i) => {
                                const year = currentYear - i;
                                return <option key={year} value={year}>{year}</option>;
                            })}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Mês</FilterLabel>
                        <FilterSelect
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"].map((m, i) => {
                                if (parseInt(selectedYear) === currentYear && i + 1 > new Date().getMonth() + 1) return null;
                                return <option key={i + 1} value={i + 1}>{m}</option>;
                            })}
                        </FilterSelect>
                    </FilterGroup>
                </FiltersContainer>
            </FiltersCard>

            {selectedCompany && (
                <>
                    <TotalizersGrid>
                        <TotalizerCard color="#00bcd4">
                            <TotalizerIcon>
                                <Receipt />
                            </TotalizerIcon>
                            <TotalizerContent>
                                <TotalizerValue>{formatAbbrValue(totals.transactions)}</TotalizerValue>
                                <TotalizerLabel>Total de Invoices</TotalizerLabel>
                                {!selectedMonth && kpiVariation.transactions !== null && (
                                    <VariationBadge positive={kpiVariation.transactions >= 0}>
                                        {kpiVariation.transactions >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.transactions).toFixed(1)}% vs ant.
                                    </VariationBadge>
                                )}
                            </TotalizerContent>
                        </TotalizerCard>

                        <TotalizerCard color="#667eea">
                            <TotalizerIcon>
                                <TrendingUp />
                            </TotalizerIcon>
                            <TotalizerContent>
                                <TotalizerValue>
                                    {formatAbbrValue(totals.totalValue, true)}
                                </TotalizerValue>
                                <TotalizerLabel>Valor Total</TotalizerLabel>
                                {!selectedMonth && kpiVariation.value !== null && (
                                    <VariationBadge positive={kpiVariation.value >= 0}>
                                        {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                                    </VariationBadge>
                                )}
                            </TotalizerContent>
                        </TotalizerCard>

                        <TotalizerCard color="#27ae60">
                            <TotalizerIcon>
                                <CalendarToday />
                            </TotalizerIcon>
                            <TotalizerContent>
                                <TotalizerValue>{formatAbbrValue(totals.averagePerMonth)}</TotalizerValue>
                                <TotalizerLabel>Média por Mês</TotalizerLabel>
                            </TotalizerContent>
                        </TotalizerCard>

                        <TotalizerCard color="#f39c12">
                            <TotalizerIcon>
                                <Assessment />
                            </TotalizerIcon>
                            <TotalizerContent>
                                <TotalizerValue>
                                    {formatAbbrValue(totals.averageAmount, true)}
                                </TotalizerValue>
                                <TotalizerLabel>Ticket Médio</TotalizerLabel>
                                {!selectedMonth && kpiVariation.ticket !== null && (
                                    <VariationBadge positive={kpiVariation.ticket >= 0}>
                                        {kpiVariation.ticket >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.ticket).toFixed(1)}% vs ant.
                                    </VariationBadge>
                                )}
                            </TotalizerContent>
                        </TotalizerCard>
                    </TotalizersGrid>

                    {metrics.length > 0 ? (
                        <>
                            {!selectedMonth && (
                                <FullWidthChartCard>
                                    <Chart
                                        chartType="ComboChart"
                                        width="100%"
                                        height="450px"
                                        data={prepareEvolutionChartData()}
                                        options={evolutionChartOptions}
                                    />
                                </FullWidthChartCard>
                            )}

                            {selectedCompany === 'all' && (
                                <FullWidthChartCard>
                                    <ChartHeader>
                                        <ChartTitle>Por Empresa</ChartTitle>
                                        <ToggleGroup>
                                            <ToggleBtn active={companyChartMode === 'count'} color="#00bcd4" onClick={() => setCompanyChartMode('count')}>Transações</ToggleBtn>
                                            <ToggleBtn active={companyChartMode === 'value'} color="#00bcd4" onClick={() => setCompanyChartMode('value')}>Valor</ToggleBtn>
                                        </ToggleGroup>
                                    </ChartHeader>
                                    <Chart
                                        chartType="ColumnChart"
                                        width="100%"
                                        height="370px"
                                        data={companyChartMode === 'count' ? prepareCompanyCountData() : prepareCompanyValueData()}
                                        options={{
                                            chartArea: { width: "70%", height: "70%" },
                                            hAxis: { title: "Empresa", titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true } },
                                            vAxis: {
                                                title: companyChartMode === 'count' ? "Invoices" : "Valor Total (R$)",
                                                titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                minValue: 0,
                                                ...(companyChartMode === 'value' ? { format: "decimal" } : {}),
                                            },
                                            colors: ["#00bcd4"],
                                            legend: { position: "none" },
                                            animation: { startup: true, duration: 1000, easing: "out" },
                                        }}
                                    />
                                </FullWidthChartCard>
                            )}

                            <TableCard>
                                <TableTitleRow>
                                    <TableTitle style={{ margin: 0 }}>Detalhamento Mensal</TableTitle>
                                    <ExportBtn onClick={exportCSV}>⬇ Exportar CSV</ExportBtn>
                                </TableTitleRow>
                                <TableWrapper>
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                {selectedCompany === 'all' && <TableHeader>Empresa</TableHeader>}
                                                <TableHeader>Mês</TableHeader>
                                                <TableHeader align="right">Invoices</TableHeader>
                                                <TableHeader align="right">Valor Total</TableHeader>
                                                <TableHeader align="right">Ticket Médio</TableHeader>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedMetrics.map((metric, index) => {
                                                    const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                                                                       "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
                                                    const monthIndex = parseInt(metric.month) - 1;
                                                    const monthName = monthNames[monthIndex] || metric.month;
                                                    const ticketMedio = metric.transactions > 0 ? (metric.totalValue / metric.transactions) : 0;
                                                    const company = empresas.find(e => e.id === metric.companyId);
                                                    const companyName = company?.name || `Empresa ${metric.companyId}`;

                                                    return (
                                                        <TableRow key={`${metric.companyId}-${metric.month}-${index}`}>
                                                            {selectedCompany === 'all' && (
                                                                <TableCell><strong>{companyName}</strong></TableCell>
                                                            )}
                                                            <TableCell><strong>{monthName}</strong></TableCell>
                                                            <TableCell align="right">
                                                                <MetricBadge color="#00bcd4">
                                                                    {metric.transactions?.toLocaleString('pt-BR') || 0}
                                                                </MetricBadge>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <MetricBadge color="#667eea">
                                                                    {(metric.totalValue || 0).toLocaleString('pt-BR', {
                                                                        style: 'currency',
                                                                        currency: 'BRL'
                                                                    })}
                                                                </MetricBadge>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <MetricBadge color="#27ae60">
                                                                    {ticketMedio.toLocaleString('pt-BR', {
                                                                        style: 'currency',
                                                                        currency: 'BRL'
                                                                    })}
                                                                </MetricBadge>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </tbody>
                                    </DataTable>
                                </TableWrapper>
                                <PaginationContainer>
                                    <PaginationInfo>
                                        Mostrando {Math.min(((currentPage - 1) * rowsPerPage) + 1, sortedMetrics.length)}-{Math.min(currentPage * rowsPerPage, sortedMetrics.length)} de {sortedMetrics.length} registros
                                    </PaginationInfo>
                                    <PaginationControls>
                                        <PaginationLabel>Linhas por página:</PaginationLabel>
                                        <PaginationSelect value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </PaginationSelect>
                                        <PaginationButton disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</PaginationButton>
                                        <PaginationPageInfo>Página {currentPage} de {totalPages}</PaginationPageInfo>
                                        <PaginationButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</PaginationButton>
                                    </PaginationControls>
                                </PaginationContainer>
                            </TableCard>
                        </>
                    ) : (
                        <EmptyStateCard>
                            <EmptyIcon>
                                <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                            </EmptyIcon>
                            <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                            <EmptyText>
                                Não há métricas de invoices para <strong>{selectedCompanyName}</strong> em <strong>{selectedYear}</strong>
                            </EmptyText>
                        </EmptyStateCard>
                    )}
                </>
            )}

            {!selectedCompany && (
                <EmptyStateContent>
                    <EmptyIcon>
                        <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                    </EmptyIcon>
                    <EmptyTitle>Selecione uma empresa</EmptyTitle>
                    <EmptyText>
                        Escolha uma empresa e um ano para visualizar as métricas de invoices
                    </EmptyText>
                </EmptyStateContent>
            )}
            </ResultsCard>
        </Container>
    );
}

export default DashboardPage;

// ===== STYLED COMPONENTS =====

const FiltersCard = styled.div`
    margin-bottom: 24px;
    padding-top: 24px;
`;

const FilterTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const FiltersContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FilterLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    font-family: 'Mulish', sans-serif;
`;

const FilterSelect = styled.select`
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #00bcd4;
        box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
    }
`;

const TotalizersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const TotalizerCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 16px;
    animation: ${fadeIn} 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }
`;

const TotalizerIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: ${props => props.color || '#00bcd4'}15;
    color: ${props => props.color || '#00bcd4'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        font-size: 28px;
    }
`;

const TotalizerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TotalizerValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const TotalizerLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const VariationBadge = styled.span`
    display: inline-block;
    margin-top: 6px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    background: ${props => props.positive ? '#dcfce7' : '#fee2e2'};
    color: ${props => props.positive ? '#15803d' : '#dc2626'};
`;

const ChartHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const ChartTitle = styled.h4`
    font-size: 15px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const ToggleGroup = styled.div`
    display: flex;
    gap: 4px;
`;

const ToggleBtn = styled.button`
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    border: 2px solid ${(props) => (props.active ? (props.color || '#00bcd4') : '#e5e7eb')};
    background: ${(props) => (props.active ? (props.color || '#00bcd4') : 'white')};
    color: ${(props) => (props.active ? 'white' : '#9ca3af')};
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
`;

const TableTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const ExportBtn = styled.button`
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    background: linear-gradient(135deg, #27ae60 0%, #219a52 100%);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
    box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const FullWidthChartCard = styled(ChartCard)`
    width: 100%;
    margin-bottom: 24px;
`;

const TableCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const TableTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const DataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    text-align: ${props => props.align || 'left'};
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
    font-family: 'Mulish', sans-serif;
`;

const TableRow = styled.tr`
    border-bottom: 1px solid #e5e7eb;
    transition: background 0.2s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
`;

const TableCell = styled.td`
    text-align: ${props => props.align || 'left'};
    padding: 16px;
    font-size: 14px;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const MetricBadge = styled.span`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: ${props => props.color || '#00bcd4'}15;
    color: ${props => props.color || '#00bcd4'};
`;

const EmptyStateCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 64px 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    text-align: center;
    animation: ${fadeIn} 0.5s ease;
`;

const EmptyStateContent = styled.div`
    text-align: center;
    padding: 64px 24px;
`;

const EmptyIcon = styled.div`
    margin-bottom: 24px;
`;

const EmptyTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 12px 0;
    font-family: 'Mulish', sans-serif;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0 0;
    margin-top: 16px;
    border-top: 1px solid #e5e7eb;
    flex-wrap: wrap;
    gap: 12px;
`;

const PaginationInfo = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PaginationLabel = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationSelect = styled.select`
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #00bcd4;
        box-shadow: 0 0 0 2px rgba(0, 188, 212, 0.1);
    }
`;

const PaginationButton = styled.button`
    padding: 8px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    color: #2c3e50;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        border-color: #00bcd4;
        color: #00bcd4;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PaginationPageInfo = styled.span`
    font-size: 13px;
    color: #2c3e50;
    font-weight: 600;
    font-family: 'Mulish', sans-serif;
`;
